import React, { useEffect, useState, useRef } from 'react';
import "./index.scss";
import { Row, Col, Card, Modal, Form } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useBreadcrumb } from '../../services/breadcrumb-services/BreadcrumbContext';
import { useLanguage } from '../../services/language/LanguageContext';
import { DateTimeFormat, IsEmptyGuid } from '../../helper/Formatters';
import ImageViewer from '../../components/image-viewer/ImageViewer';
import { toast } from 'react-toastify';
import CommentsBox from '../../components/comments-box';
import ImageUpload from '../../components/image-upload';
import AccountsBox from '../../components/accounts-box';
import RecentTransactionsBox from '../../components/recent-transactions-box';

// ─── Info Row ────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, children }) => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 10, fontSize: 14 }}>
        <span style={{ color: '#6b7280', minWidth: 100, flexShrink: 0 }}>{label}</span>
        <span style={{ color: '#1f2937', fontWeight: 500 }}>{children || value || '-'}</span>
    </div>
);

// ─── Product Dropdown ─────────────────────────────────────────────────────────
const AVATAR_COLORS = ['#1e3a5f', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626'];
const getAvatarColor = (str = '') => AVATAR_COLORS[(str.charCodeAt(0) || 0) % AVATAR_COLORS.length];

const ProductDropdown = ({ products, value, onChange, placeholder, disabledIds = [] }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = products.filter(p =>
        `${p.nameTh || ''} ${p.nameEn || ''} ${p.sku || ''}`.toLowerCase().includes(search.toLowerCase())
    );
    const selected = products.find(p => p.id === value) || null;
    const selColor = selected ? getAvatarColor(selected.nameTh || '') : null;

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setOpen(v => !v)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '7px 12px', borderRadius: 8,
                border: `1.5px solid ${open ? '#2d6a9f' : '#dee2e6'}`,
                background: '#fff', cursor: 'pointer', textAlign: 'left',
                boxShadow: open ? '0 0 0 3px rgba(45,106,159,0.1)' : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
            }}>
                {selected ? (
                    selected.image ? (
                        <div style={{ width: 28, height: 28, borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                            <ImageViewer id={selected.image} width={28} preview={false} />
                        </div>
                    ) : (
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: `linear-gradient(135deg, ${selColor}cc, ${selColor})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                            {(selected.nameTh || '?')[0]}
                        </div>
                    )
                ) : (
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: '#f3f4f6', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                        <i className="fas fa-box" />
                    </div>
                )}
                <span style={{ flex: 1, fontSize: 14, color: selected ? '#1f2937' : '#9ca3af' }}>
                    {selected ? selected.nameTh : placeholder}
                </span>
                <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 11, color: '#9ca3af' }} />
            </button>

            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1060,
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
                }}>
                    <div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ position: 'relative' }}>
                            <i className="fa fa-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 11, pointerEvents: 'none' }} />
                            <input
                                autoFocus
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search..."
                                style={{ width: '100%', paddingLeft: 28, paddingRight: 10, paddingTop: 6, paddingBottom: 6, border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, outline: 'none', color: '#374151' }}
                            />
                        </div>
                    </div>
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        {filtered.length === 0 ? (
                            <div style={{ padding: '14px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>ไม่พบสินค้า</div>
                        ) : filtered.map(p => {
                            const disabled = disabledIds.includes(p.id);
                            const ac = getAvatarColor(p.nameTh || '');
                            const isSelected = p.id === value;
                            return (
                                <button
                                    key={p.id}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => { if (!disabled) { onChange(p.id); setOpen(false); setSearch(''); } }}
                                    style={{
                                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '8px 12px', border: 'none', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
                                        background: isSelected ? '#e0e7ff' : 'transparent',
                                        opacity: disabled ? 0.45 : 1,
                                        borderBottom: '1px solid #f9fafb',
                                    }}
                                >
                                    {p.image ? (
                                        <div style={{ width: 32, height: 32, borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                                            <ImageViewer id={p.image} width={32} preview={false} />
                                        </div>
                                    ) : (
                                        <div style={{ width: 32, height: 32, borderRadius: 6, background: `linear-gradient(135deg, ${ac}22, ${ac}44)`, color: ac, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                            {(p.nameTh || '?')[0]}
                                        </div>
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {p.nameTh}
                                        </div>
                                        {p.sku && <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{p.sku}</div>}
                                    </div>
                                    {disabled && <span style={{ fontSize: 10, color: '#6b7280', background: '#f3f4f6', borderRadius: 6, padding: '1px 6px', whiteSpace: 'nowrap' }}>เพิ่มแล้ว</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Owner Dropdown ──────────────────────────────────────────────────────────
const OwnerDropdown = ({ users, value, onChange, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = users.filter(u =>
        `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(search.toLowerCase())
    );
    const selected = users.find(u => u.id === value) || null;
    const selInitials = selected ? `${(selected.firstName || '')[0] || ''}${(selected.lastName || '')[0] || ''}`.toUpperCase() : null;
    const selColor = selected ? getAvatarColor(selected.firstName || '') : null;

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setOpen(v => !v)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '7px 12px', borderRadius: 8,
                border: `1.5px solid ${open ? '#2d6a9f' : '#dee2e6'}`,
                background: '#fff', cursor: 'pointer', textAlign: 'left',
                boxShadow: open ? '0 0 0 3px rgba(45,106,159,0.1)' : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
            }}>
                {selected ? (
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: `linear-gradient(135deg, ${selColor}cc, ${selColor})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                        {selInitials}
                    </div>
                ) : (
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#f3f4f6', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>
                        <i className="fa fa-user" />
                    </div>
                )}
                <span style={{ flex: 1, fontSize: 14, color: selected ? '#1f2937' : '#9ca3af' }}>
                    {selected ? `${selected.firstName || ''} ${selected.lastName || ''}`.trim() : placeholder}
                </span>
                <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 11, color: '#9ca3af' }} />
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1060,
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
                }}>
                    <div style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ position: 'relative' }}>
                            <i className="fa fa-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 11, pointerEvents: 'none' }} />
                            <input
                                autoFocus
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search..."
                                style={{ width: '100%', paddingLeft: 28, paddingRight: 10, paddingTop: 6, paddingBottom: 6, border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, outline: 'none', color: '#374151' }}
                            />
                        </div>
                    </div>
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        {filtered.length === 0 ? (
                            <div style={{ padding: '14px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>ไม่พบผู้ใช้</div>
                        ) : filtered.map(u => {
                            const initials = `${(u.firstName || '')[0] || ''}${(u.lastName || '')[0] || ''}`.toUpperCase();
                            const ac = getAvatarColor(u.firstName || '');
                            const sel = u.id === value;
                            return (
                                <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => { onChange(u.id); setOpen(false); setSearch(''); }}
                                    style={{
                                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '8px 12px', border: 'none', textAlign: 'left', cursor: 'pointer',
                                        background: sel ? '#e0e7ff' : 'transparent',
                                        borderBottom: '1px solid #f9fafb',
                                    }}
                                >
                                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: `linear-gradient(135deg, ${ac}cc, ${ac})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                        {initials}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{u.firstName} {u.lastName}</div>
                                        {u.phone && <div style={{ fontSize: 11, color: '#9ca3af' }}>{u.phone}</div>}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const StoresDetails = () => {
    const { id } = useParams();
    const api = useApiServices();
    const { setCustomName } = useBreadcrumb();
    const { t } = useLanguage();
    const [data, setData] = useState({});
    const [transactions, setTransactions] = useState([]);
    const [products, setProducts] = useState([]);
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [formData, setFormData] = useState({
        id: '', productId: '', stock: 0, costPrice: 0, price: 0, discount: 0,
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState({
        latitude: '', longitude: '', radiusKm: '', userId: '', name: '', image: '',
    });

    const handleClose = () => {
        setShowModal(false);
        setFormData({ id: '', productId: '', stock: 0, costPrice: 0, price: 0, discount: 0 });
    };

    const handleShow = (stockItem = null) => {
        if (stockItem) setFormData(stockItem);
        setShowModal(true);
    };

    const getProduct = (productId) => products.find(p => p.id === productId);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.productId) { toast.error(t('select_product')); return; }
        setSubmitting(true);
        const isEdit = formData.id && formData.id !== '';
        const { isSuccess } = isEdit
            ? await api.UpdateConfigurationsAsync(formData.id, formData)
            : await api.AddConfigurationsAsync({ ...formData, storeId: id, id: null });
        setSubmitting(false);
        if (isSuccess) {
            toast.success(isEdit ? t('save') : t('add_product_to_store'));
            handleClose();
            fetchStore(id);
        } else {
            toast.error('บางอย่างผิดพลาด');
        }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditData(prev => ({ ...prev, [name]: value }));
    };

    const handleEditSubmit = async () => {
        setEditSubmitting(true);
        const { isSuccess } = await api.UpdateStoresAsync(id, {
            ...editData,
            image: file?.id ?? (data.image ?? null),
        });
        setEditSubmitting(false);
        if (isSuccess) {
            toast.success('บันทึกข้อมูลแล้ว');
            fetchStore(id);
            setShowEditModal(false);
        } else {
            toast.error('บางอย่างผิดพลาด');
        }
    };

    const fetchStore = async (storeId) => {
        const { isSuccess, result } = await api.GetStoresDetailAsync(storeId);
        if (isSuccess) {
            setData(result);
            setCustomName(result.name);
            const [recentRes, productsRes] = await Promise.all([
                api.GetRecentTransactionsAsync(storeId),
                api.GetProductsAsync(),
            ]);
            if (recentRes.isSuccess) setTransactions(recentRes.result);
            if (productsRes.isSuccess) setProducts(productsRes.result.items);
        }
    };

    const fetchUsers = async () => {
        const { isSuccess, result } = await api.GetUserListAsync({ pageNumber: 1, pageSize: 500 });
        if (isSuccess) setUsers(result.items);
    };

    useEffect(() => { if (id) fetchStore(id); }, [id]);

    useEffect(() => {
        if (showEditModal) {
            setEditData({
                latitude: data.latitude ?? '',
                longitude: data.longitude ?? '',
                radiusKm: data.radiusKm ?? '',
                userId: data.userId ?? '',
                name: data.name ?? '',
            });
            fetchUsers();
        }
    }, [showEditModal]);

    const configs = data.productToStoreConfigurations || [];

    return (
        <React.Fragment>
            <Row className="g-3">
                {/* ── Left Column ─────────────────────────────────────── */}
                <Col md={4}>
                    {/* Store Profile */}
                    <Card style={cardStyle} className="mb-3">
                        <Card.Body style={{ textAlign: 'center', padding: '24px 20px' }}>
                            <div style={{ display: 'inline-block', borderRadius: '50%', overflow: 'hidden', border: '3px solid #e0e7ff', marginBottom: 12 }}>
                                <ImageViewer id={data.image} width={80} preview={false} />
                            </div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: '#1e3a5f', marginBottom: 4 }}>
                                {data.name || '-'}
                            </div>
                            <span style={{ fontSize: 12, background: '#e0e7ff', color: '#4f46e5', borderRadius: 10, padding: '2px 10px', fontWeight: 600 }}>
                                {configs.length} สินค้า
                            </span>
                        </Card.Body>
                    </Card>

                    {/* General Info */}
                    <Card style={cardStyle} className="mb-3">
                        <Card.Body>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                <span style={sectionTitle}>{t('general_info')}</span>
                                <button onClick={() => setShowEditModal(true)} style={editBtnStyle}>
                                    <i className="fa fa-pencil" style={{ fontSize: 10 }} />{t('edit')}
                                </button>
                            </div>
                            <InfoRow label={t('owner_label')}>
                                {data.user ? (
                                    <Link to={`/users/${data.userId}`} style={{ color: '#1e3a5f', fontWeight: 600, textDecoration: 'none' }}>
                                        {data.user.firstName} {data.user.lastName}
                                    </Link>
                                ) : '-'}
                            </InfoRow>
                            <InfoRow label={t('col_phone')}>{data.user?.phone}</InfoRow>
                            <InfoRow label={t('latitude')}>{data.latitude}</InfoRow>
                            <InfoRow label={t('longitude')}>{data.longitude}</InfoRow>
                            <InfoRow label={t('radius_km')}>{data.radiusKm ? `${data.radiusKm} km` : undefined}</InfoRow>
                        </Card.Body>
                    </Card>

                    {/* Recent Transactions */}
                    <Card style={cardStyle}>
                        <Card.Body>
                            <RecentTransactionsBox data={transactions} />
                        </Card.Body>
                    </Card>
                </Col>

                {/* ── Right Column ────────────────────────────────────── */}
                <Col md={8}>
                    {/* Accounts */}
                    <Card style={cardStyle} className="mb-3">
                        <Card.Body>
                            <AccountsBox euid={data.entityUniqueIdentifierId} />
                        </Card.Body>
                    </Card>

                    {/* Product List */}
                    <Card style={cardStyle} className="mb-3">
                        <Card.Body style={{ padding: 0 }}>
                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={sectionTitle}>{t('products_title')}</span>
                                    {configs.length > 0 && (
                                        <span style={{ fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#4f46e5', borderRadius: 10, padding: '1px 7px' }}>
                                            {configs.length}
                                        </span>
                                    )}
                                </div>
                                <button onClick={() => handleShow()} style={addBtnStyle}>
                                    <i className="fa fa-plus" style={{ fontSize: 10 }} />{t('add_product')}
                                </button>
                            </div>

                            {/* Table */}
                            {configs.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: 13 }}>
                                    <i className="fas fa-box-open" style={{ fontSize: 24, marginBottom: 8, display: 'block', opacity: 0.3 }} />
                                    ยังไม่มีสินค้าในร้านนี้
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                        <thead>
                                            <tr>
                                                {[t('col_products'), t('stock_qty'), t('cost_price'), t('selling_price'), t('margin_label'), t('col_date'), ''].map(h => (
                                                    <th key={h} style={thStyle}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {configs.map(stock => {
                                                const product = getProduct(stock.productId);
                                                const margin = stock.price - stock.costPrice;
                                                const marginPct = stock.costPrice > 0 ? ((margin / stock.costPrice) * 100).toFixed(0) : null;
                                                const lowStock = stock.stock !== null && stock.stock <= 5;

                                                return (
                                                    <tr
                                                        key={stock.id}
                                                        onMouseEnter={() => setHoveredRow(stock.id)}
                                                        onMouseLeave={() => setHoveredRow(null)}
                                                        style={{ background: hoveredRow === stock.id ? '#f9fafb' : '#fff', transition: 'background 0.1s' }}
                                                    >
                                                        {/* Product name */}
                                                        <td style={tdStyle}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                {product?.image ? (
                                                                    <ImageViewer id={product.image} width={36} preview={false} />
                                                                ) : (
                                                                    <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                        <i className="fa fa-image" style={{ color: '#d1d5db', fontSize: 13 }} />
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <div style={{ fontWeight: 600, color: '#1f2937' }}>
                                                                        {product?.nameTh || stock.productId}
                                                                    </div>
                                                                    {product?.sku && (
                                                                        <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{product.sku}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* Stock */}
                                                        <td style={tdStyle}>
                                                            <span style={{
                                                                fontSize: 12, fontWeight: 600, borderRadius: 8, padding: '2px 8px',
                                                                background: lowStock ? '#fef2f2' : '#f0fdf4',
                                                                color: lowStock ? '#dc2626' : '#16a34a',
                                                                border: `1px solid ${lowStock ? '#fecaca' : '#bbf7d0'}`,
                                                            }}>
                                                                {stock.stock ?? '-'}
                                                            </span>
                                                        </td>

                                                        {/* Cost */}
                                                        <td style={tdStyle}>
                                                            <span style={{ color: '#6b7280' }}>{stock.costPrice?.toLocaleString() ?? '-'}</span>
                                                        </td>

                                                        {/* Price */}
                                                        <td style={tdStyle}>
                                                            <span style={{ fontWeight: 600 }}>{stock.price?.toLocaleString() ?? '-'}</span>
                                                            {stock.discount > 0 && (
                                                                <div style={{ fontSize: 11, color: '#f97316' }}>ลด {stock.discount}</div>
                                                            )}
                                                        </td>

                                                        {/* Margin */}
                                                        <td style={tdStyle}>
                                                            <div style={{ fontWeight: 600, color: margin >= 0 ? '#16a34a' : '#dc2626', fontSize: 12 }}>
                                                                +{margin.toLocaleString()}
                                                            </div>
                                                            {marginPct !== null && (
                                                                <div style={{ fontSize: 10, color: '#9ca3af' }}>{marginPct}%</div>
                                                            )}
                                                        </td>

                                                        {/* Date */}
                                                        {/* <td style={{ ...tdStyle, color: '#9ca3af', fontSize: 11 }}>
                                                            {DateTimeFormat(stock.modifyDate)}
                                                        </td> */}

                                                        {/* Edit */}
                                                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                            <button
                                                                onClick={() => handleShow(stock)}
                                                                style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 5,
                                                                    padding: '4px 10px', background: '#f0f4ff',
                                                                    border: '1px solid #c7d2fe', borderRadius: 7,
                                                                    color: '#4f46e5', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                                                }}
                                                            >
                                                                <i className="fa fa-pencil" style={{ fontSize: 9 }} />{t('edit')}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Comments */}
                    {!IsEmptyGuid(data.entityUniqueIdentifierId) && (
                        <Card style={cardStyle}>
                            <Card.Body>
                                <CommentsBox euid={data.entityUniqueIdentifierId} />
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>

            {/* ── Add/Edit Product Modal ───────────────────────────── */}
            <Modal show={showModal} onHide={handleClose} centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <i className="fas fa-box" style={{ color: '#fff', fontSize: 14 }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e3a5f' }}>
                                {formData.id ? t('edit_product_in_store') : t('add_product_to_store')}
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>{data.name}</div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px' }}>
                    {/* Product selector */}
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>{t('col_products')}</Form.Label>
                        {formData.id ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                                {(() => {
                                    const p = products.find(x => x.id === formData.productId);
                                    return p ? (
                                        <>
                                            {p.image ? <div style={{ width: 28, height: 28, borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}><ImageViewer id={p.image} width={28} preview={false} /></div> : null}
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{p.nameTh}</div>
                                                {p.sku && <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{p.sku}</div>}
                                            </div>
                                        </>
                                    ) : <span style={{ fontSize: 13, color: '#6b7280' }}>{formData.productId}</span>;
                                })()}
                            </div>
                        ) : (
                            <ProductDropdown
                                products={products}
                                value={formData.productId}
                                onChange={(pid) => setFormData(prev => ({ ...prev, productId: pid }))}
                                placeholder={t('select_product')}
                                disabledIds={(data.productToStoreConfigurations || []).map(c => c.productId)}
                            />
                        )}
                    </Form.Group>

                    {/* Price row */}
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label style={labelStyle}>{t('cost_price')} (฿)</Form.Label>
                                <Form.Control type="number" name="costPrice" value={formData.costPrice} onChange={handleChange} style={{ fontSize: 14, borderRadius: 8 }} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label style={labelStyle}>{t('selling_price')} (฿)</Form.Label>
                                <Form.Control type="number" name="price" value={formData.price} onChange={handleChange} style={{ fontSize: 14, borderRadius: 8 }} />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Margin preview */}
                    {formData.costPrice > 0 && formData.price > 0 && (() => {
                        const m = Number(formData.price) - Number(formData.costPrice);
                        const pct = formData.costPrice > 0 ? ((m / formData.costPrice) * 100).toFixed(1) : 0;
                        return (
                            <div style={{ background: m >= 0 ? '#f0fdf4' : '#fef2f2', border: `1px solid ${m >= 0 ? '#bbf7d0' : '#fecaca'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                                <span style={{ color: '#6b7280', fontWeight: 600 }}>{t('margin_label')}</span>
                                <span style={{ fontWeight: 700, color: m >= 0 ? '#16a34a' : '#dc2626', fontSize: 15 }}>
                                    {m >= 0 ? '+' : ''}{m.toLocaleString()} ฿
                                    <span style={{ fontSize: 12, fontWeight: 500, marginLeft: 6, color: m >= 0 ? '#059669' : '#ef4444' }}>({pct}%)</span>
                                </span>
                            </div>
                        );
                    })()}

                    {/* Stock & Discount row */}
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label style={labelStyle}>{t('stock_qty')}</Form.Label>
                                <Form.Control type="number" name="stock" value={formData.stock} onChange={handleChange} style={{ fontSize: 14, borderRadius: 8 }} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-0">
                                <Form.Label style={labelStyle}>{t('discount_label')} (฿)</Form.Label>
                                <Form.Control type="number" name="discount" value={formData.discount} onChange={handleChange} style={{ fontSize: 14, borderRadius: 8 }} />
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', gap: 8, padding: '12px 20px' }}>
                    <button onClick={handleClose} style={cancelBtnStyle}>{t('cancel')}</button>
                    <button onClick={handleSubmit} disabled={submitting} style={saveBtnStyle}>
                        {submitting ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />{t('saving')}</> : <><i className="fa fa-check" style={{ marginRight: 6 }} />{t('save')}</>}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* ── Edit Store Modal ─────────────────────────────────── */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <i className="fas fa-store" style={{ color: '#fff', fontSize: 14 }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e3a5f' }}>{t('edit_store_info')}</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>{data.name}</div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px' }}>
                    {/* Section: Basic */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                        <div style={{ width: 24, height: 24, borderRadius: 6, background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fa fa-info-circle" style={{ fontSize: 11, color: '#4f46e5' }} />
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('general_info')}</span>
                        <div style={{ flex: 1, height: 1, background: '#f3f4f6' }} />
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>{t('store_image')}</Form.Label>
                        <ImageUpload forceCrop={true} onUploadComplete={(f) => setFile(f)} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>{t('store_owner')}</Form.Label>
                        <OwnerDropdown
                            users={users}
                            value={editData.userId}
                            onChange={(uid) => setEditData(prev => ({ ...prev, userId: uid }))}
                            placeholder={t('select_owner')}
                        />
                    </Form.Group>
                    <Form.Group className="mb-4">
                        <Form.Label style={labelStyle}>{t('store_name')}</Form.Label>
                        <Form.Control type="text" name="name" value={editData.name} onChange={handleEditChange} style={{ fontSize: 14, borderRadius: 8 }} />
                    </Form.Group>

                    {/* Section: Location */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                        <div style={{ width: 24, height: 24, borderRadius: 6, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fas fa-map-marker-alt" style={{ fontSize: 11, color: '#16a34a' }} />
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('location_info')}</span>
                        <div style={{ flex: 1, height: 1, background: '#f3f4f6' }} />
                    </div>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label style={labelStyle}>{t('latitude')}</Form.Label>
                                <Form.Control type="text" name="latitude" value={editData.latitude} onChange={handleEditChange} style={{ fontSize: 14, borderRadius: 8 }} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label style={labelStyle}>{t('longitude')}</Form.Label>
                                <Form.Control type="text" name="longitude" value={editData.longitude} onChange={handleEditChange} style={{ fontSize: 14, borderRadius: 8 }} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-0">
                        <Form.Label style={labelStyle}>{t('radius_km')}</Form.Label>
                        <Form.Control type="number" name="radiusKm" value={editData.radiusKm} onChange={handleEditChange} style={{ fontSize: 14, borderRadius: 8 }} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', gap: 8, padding: '12px 20px' }}>
                    <button onClick={() => setShowEditModal(false)} style={cancelBtnStyle}>{t('cancel')}</button>
                    <button onClick={handleEditSubmit} disabled={editSubmitting} style={saveBtnStyle}>
                        {editSubmitting ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />{t('saving')}</> : <><i className="fa fa-check" style={{ marginRight: 6 }} />{t('save')}</>}
                    </button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const cardStyle = { borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' };
const sectionTitle = { fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' };
const thStyle = { padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const tdStyle = { padding: '12px 16px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' };
const addBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const editBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 7, color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const cancelBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 16px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const saveBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 18px', background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };

export default StoresDetails;
